/// <amd-module name="Wit/Search/SearchHelp" />
import React = require("react");
import { VssComponent } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ISearchInput } from "VSS/Platform/Search";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { SearchHelpComponent, ISearchFilterGroup, ISearchFilter } from "VSS/Features/Search/SearchHelp";
import { FieldSuggestionStore, DropdownType, defaultFieldsFilterGroup, defaultOperatorsFilterGroup } from "Wit/Search/FieldSuggestionStore";
import { WorkItemTrackingFieldService } from "Wit/Search/SearchFields";
import { WITIdentityPicker } from "Wit/Search/WitPeoplePicker";

export interface IWitSearchHelpComponentProps {
    searchInput: ISearchInput;
}

export interface IWitSearchHelpComponentState {
    filterGroups: ISearchFilterGroup[];
    filterText: string;
    dropdownType: DropdownType;
}

export class WorkitemSearchHelpComponent extends VssComponent<IWitSearchHelpComponentProps, IWitSearchHelpComponentState> {
    private fieldSuggestionStore: FieldSuggestionStore;
    constructor(props: IWitSearchHelpComponentProps) {
        super(props);
        this.state = {
            filterGroups: [],
            filterText: "",
            dropdownType: DropdownType.None
        };

        this.fieldSuggestionStore = new FieldSuggestionStore();
    }

    public render(): JSX.Element | null {
        const { filterGroups, filterText, dropdownType } = this.state;
        if (dropdownType !== DropdownType.Identity) {
            return (
                <SearchHelpComponent
                    filterGroups={filterGroups}
                    helpLink="https://docs.microsoft.com/en-us/azure/devops/project/search/functional-work-item-search?view=azure-devops"
                    searchInput={this.props.searchInput}
                    filterText={filterText}
                    onRenderFilterText={onRenderFilterText}
                    onItemActivated={this._notifyFilterActivated}
                />
            );
        } else if (dropdownType === DropdownType.Identity) {
            return (
                <WITIdentityPicker
                    searchInput={this.props.searchInput}
                    searchText={filterText}
                />
            );
        }

        return null;
    }

    public componentDidMount(): void {
        const queryText = this.props.searchInput.queryText();
        if (queryText) {
            this.onChange(queryText);
        } else {
            this.setState({
                filterGroups: [
                    defaultFieldsFilterGroup,
                    defaultOperatorsFilterGroup
                ]
            });
        }
        this.props.searchInput.subscribe(this.onChange);
    }

    public componentWillUnmount(): void {
        this.props.searchInput.unsubscribe(this.onChange);
    }

    private onChange = (value: string) => {
        const witFieldsService = this.context.pageContext.getService<WorkItemTrackingFieldService>("WorkItemTrackingFieldService");
        witFieldsService.getFields().then(suggestionFields => {
            const filterGroups = this.fieldSuggestionStore.getFilterGroups(value, suggestionFields);
            const filterText = this.fieldSuggestionStore.textToFilter;
            const dropdownType = this.fieldSuggestionStore.dropdownType;
            const hideHelp = this.fieldSuggestionStore.dropdownType === DropdownType.None ? true : false;
            if (hideHelp) {
                this.props.searchInput.setHelpVisibility(hideHelp);
            } else {
                this.setState({ filterGroups, filterText, dropdownType });
            }
        });
    }

    private _notifyFilterActivated = (filter: ISearchFilter): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("L1SearchBox", "WorkItem.SearchHelpFilterActivated", {
            filterText: filter && filter.text,
            entity: "WorkItem"
        });
    }
}

const onRenderFilterText = (filter: ISearchFilter, filterText: string): JSX.Element | string => {
    filterText = filterText.replace(/[\s]+/g, " ").toLowerCase();
    const hasSpace = filterText.search(/\s/) > 0;

    if (!filterText) {
        return filter.text;
    }

    let highlightedText, nonHighligtedText;

    if (hasSpace) {
        highlightedText = filter.text.substring(filterText.length);
        nonHighligtedText = filter.text.substring(0, filterText.length);
    } else {
        let indexOfSpace = filter.text.indexOf(" ");
        let filterTextLength = filterText.length;
        while (indexOfSpace > 0) {
            if (filterTextLength > indexOfSpace) {
                filterTextLength += 1;
                indexOfSpace = filter.text.indexOf(" ", indexOfSpace + 1);
            } else {
                break;
            }
        }

        highlightedText = filter.text.substring(filterTextLength);
        nonHighligtedText = filter.text.substring(0, filterTextLength);
    }

    return (
        <FormatComponent format={`${nonHighligtedText}{0}`}>
            <span style={{ fontWeight: 700 }}>{highlightedText}</span>
        </FormatComponent>
    );
}

VssComponent.register("search.workItem.help", WorkitemSearchHelpComponent);